import {
  CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL,
  COMPOSE_EMAIL_TOOL,
  READ_CURRENT_PAGE_TOOL,
} from '@realadvisor/common-types';
import {
  lastAssistantMessageIsCompleteWithApprovalResponses,
  lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai';

// Decides which still-waiting tool calls the CRM may settle itself once a
// chat stream stops.
//
// The browser runs `navigate_to` (`onToolCall` already reports its result),
// `ask_clarifying_questions` and `compose_email` (both answered only by the
// user, from their cards). Approval-gated tools stay waiting so approve/deny
// can still arrive. A server tool that executes on its own (search_docs,
// read_doc, graphql_query, …) and is still `input-available` after the stream
// has stopped will not get a result on this connection.
// Leaving it `input-available` keeps the tool row on its spinner. Settle it
// with the same interrupted text the chat API writes when it repairs a
// dangling tool call (`TOOL_RESULT_RECOVERED_TEXT` in
// crm-chat-api/utils/sanitize-tool-calls.ts), so the row stops and the model
// can retry. Do not use the unsupported-client error for those: that text
// makes the model answer as if the tool does not exist.
//
// An unknown client tool (a newer server deploy added one this build does
// not implement) still gets the structured unsupported error.
//
// Names are the inventory in crm-chat-api/tools/approval.node-test.ts.
// Prefixes cover a namespaced tool that ships before this list is updated.
// That test fails if a new server tool is missing from the set below.

// `ask_clarifying_questions` and `compose_email` wait for the user's answer in
// their cards; they must never be settled automatically. Also sent as
// `clientTools` in the chat context: the chat API registers
// ask_clarifying_questions only for an app that lists it.
export const IMPLEMENTED_CLIENT_TOOLS: ReadonlySet<string> = new Set([
  'navigate_to',
  'ask_clarifying_questions',
  COMPOSE_EMAIL_TOOL,
  CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL,
  READ_CURRENT_PAGE_TOOL,
]);

const SERVER_EXECUTED_TOOLS = new Set([
  'get_crm_capabilities',
  'query_crm_data',
  'get_crm_object',
  'get_market_data_capabilities',
  'query_market_data',
  'get_market_data_object',
  'graphql_explore_schema',
  'graphql_query',
  'graphql_mutation',
  'market_data_explore_schema',
  'market_data_query',
  // The two names the market-data tools had before: a chat saved under
  // the old names settles the same way.
  'scrapers_explore_schema',
  'scrapers_query',
  'search_docs',
  'read_doc',
  'load_skill',
  'fetch_url',
  'web_search',
  'add_property_images',
  'get_document_theme',
  'look_at_team_website',
  'preview_team_theme',
  'save_team_theme',
  'delete_team_theme',
  'get_document_template',
  'update_document_template',
  'undo_document_template_change',
  'look_at_report_pages',
  'render_visual',
  'generate_image',
  'dns_lookup',
  'mail_settings_detect',
  'mail_server_probe',
  'save_artifact',
  'list_artifacts',
  'read_artifact',
  'show_files',
  'show_crm_images',
  'sandbox_exec',
  'sandbox_write_file',
  'sandbox_read_file',
  'load_artifact',
  'load_attachment',
  'load_listing_images',
  'load_team_theme',
  'load_document_theme',
  'preview_document',
]);

export const isServerExecutedTool = (toolName: string): boolean =>
  SERVER_EXECUTED_TOOLS.has(toolName) ||
  toolName.startsWith('graphql_') ||
  toolName.startsWith('market_data_') ||
  toolName.startsWith('scrapers_') ||
  toolName.endsWith('_mutation');

// Same sentence as crm-chat-api `TOOL_RESULT_RECOVERED_TEXT`. Kept here so the
// client settlement and the server repair tell the model the same thing.
export const INTERRUPTED_TOOL_ERROR =
  'Tool result was not recorded (the previous run was interrupted). Treat this tool call as failed and continue.';

const isApprovalGatedTool = (toolName: string): boolean =>
  toolName === 'add_property_images' ||
  toolName === 'preview_team_theme' ||
  toolName === 'save_team_theme' ||
  toolName === 'delete_team_theme' ||
  toolName === 'create_team_template' ||
  toolName.endsWith('_mutation');

export type PendingToolPart = {
  type: string;
  state?: string;
  toolName?: string;
  toolCallId?: string;
  approval?: unknown;
  // Present on real tool parts. The helper ignores them; the fixtures include
  // them so the test objects match a waiting or finished call.
  input?: unknown;
  output?: unknown;
};

export type UnsupportedClientToolResult = {
  tool: string;
  toolCallId: string;
  output: { ok: false; error: string };
};

export type InterruptedServerToolResult = {
  tool: string;
  toolCallId: string;
  state: 'output-error';
  errorText: string;
};

export type SettledToolResult =
  | UnsupportedClientToolResult
  | InterruptedServerToolResult;

const toolNameOf = (part: PendingToolPart): string =>
  part.type === 'dynamic-tool'
    ? part.toolName ?? ''
    : part.type.startsWith('tool-')
    ? part.type.slice('tool-'.length)
    : part.toolName ?? '';

const isToolPart = (part: PendingToolPart): boolean =>
  part.type.startsWith('tool-') || part.type === 'dynamic-tool';

export const unsupportedClientToolResults = (
  parts: ReadonlyArray<PendingToolPart>,
  alreadyResponded: ReadonlySet<string>,
): Array<SettledToolResult> => {
  const results: Array<SettledToolResult> = [];
  for (const part of parts) {
    if (!isToolPart(part) || part.state !== 'input-available') {
      continue;
    }
    const toolCallId = part.toolCallId;
    if (toolCallId == null || alreadyResponded.has(toolCallId)) {
      continue;
    }
    // An approval object means the server is waiting on the user. Settling
    // the call here would skip approve/deny.
    if (part.approval != null) {
      continue;
    }
    const toolName = toolNameOf(part);
    if (
      IMPLEMENTED_CLIENT_TOOLS.has(toolName) ||
      isApprovalGatedTool(toolName)
    ) {
      continue;
    }
    if (isServerExecutedTool(toolName)) {
      results.push({
        tool: toolName,
        toolCallId,
        state: 'output-error',
        errorText: INTERRUPTED_TOOL_ERROR,
      });
      continue;
    }
    results.push({
      tool: toolName,
      toolCallId,
      output: {
        ok: false,
        error: `The client tool '${toolName}' is not supported by this CRM version.`,
      },
    });
  }
  return results;
};

type AgentMessages = Parameters<
  typeof lastAssistantMessageIsCompleteWithToolCalls
>[0]['messages'];

type LoosePart = {
  type?: string;
  state?: string;
  errorText?: string;
  providerExecuted?: boolean;
};

// Tool parts in the last model step. Matches the AI SDK resume check, which
// ignores earlier steps and provider-executed calls.
const lastStepToolParts = (messages: AgentMessages): LoosePart[] => {
  const message = messages.at(-1);
  if (!message || message.role !== 'assistant') {
    return [];
  }
  const parts = message.parts as LoosePart[];
  let start = 0;
  parts.forEach((part, index) => {
    if (part.type === 'step-start') {
      start = index + 1;
    }
  });
  return parts
    .slice(start)
    .filter(
      part =>
        (part.type?.startsWith('tool-') || part.type === 'dynamic-tool') &&
        part.providerExecuted !== true,
    );
};

// True when this step's only tool outputs are the interrupted note we write
// after a drop. A real tool result or an approval is not this case.
export const lastStepIsInterruptedSettle = (
  messages: AgentMessages,
): boolean => {
  const tools = lastStepToolParts(messages);
  return (
    tools.length > 0 &&
    tools.every(
      part =>
        part.state === 'output-error' &&
        part.errorText === INTERRUPTED_TOOL_ERROR,
    )
  );
};

// Apply settle results onto the last assistant message. The React state has
// not caught up yet when the effect decides whether this settle spent the
// one automatic retry.
export const messagesWithSettledResults = (
  messages: AgentMessages,
  results: ReadonlyArray<SettledToolResult>,
): AgentMessages => {
  const lastIndex = messages.length - 1;
  const last = messages[lastIndex];
  if (!last || last.role !== 'assistant' || results.length === 0) {
    return messages;
  }
  const byId = new Map(results.map(result => [result.toolCallId, result]));
  const parts = last.parts.map(part => {
    const toolCallId =
      'toolCallId' in part && typeof part.toolCallId === 'string'
        ? part.toolCallId
        : undefined;
    const result = toolCallId == null ? undefined : byId.get(toolCallId);
    if (!result) {
      return part;
    }
    if ('state' in result) {
      return {
        ...part,
        state: result.state,
        errorText: result.errorText,
      };
    }
    return {
      ...part,
      state: 'output-available' as const,
      output: result.output,
    };
  }) as typeof last.parts;
  const next = messages.slice();
  next[lastIndex] = { ...last, parts };
  return next;
};

// Spend the one automatic retry only when the whole step is now the
// interrupted note. One tool marked early, while another is still waiting,
// must not consume it.
export const interruptedRetriesAfterSettle = (
  messages: AgentMessages,
  userStopped: boolean,
  interruptedRetries: number,
): number => {
  if (
    !userStopped &&
    interruptedRetries === 0 &&
    lastStepIsInterruptedSettle(messages)
  ) {
    return 1;
  }
  return interruptedRetries;
};

// Whether settling a tool (or an approval) may start another model turn.
// A user Stop sets `userStopped`: the row can still be marked interrupted,
// but the loop must not restart until the user sends, edits, regenerates,
// or answers an approval. A crash leaves that flag unset, so one dropped
// server tool can be retried. `interruptedRetries` counts those automatic
// retries already spent since the last user action. A later drop in that
// same stretch waits, even when a successful step came in between.
export const agentLoopMayContinue = (
  messages: AgentMessages,
  userStopped: boolean,
  interruptedRetries = 0,
): boolean => {
  if (userStopped) {
    return false;
  }
  const mayContinue =
    lastAssistantMessageIsCompleteWithToolCalls({ messages }) ||
    lastAssistantMessageIsCompleteWithApprovalResponses({ messages });
  if (!mayContinue) {
    return false;
  }
  if (lastStepIsInterruptedSettle(messages) && interruptedRetries >= 1) {
    return false;
  }
  return true;
};

// "Approve all" must not answer a waiting card while a user Stop is in force.
// Answering it would mark the row approved and then refuse to run the change.
// The button stays until the user clicks it, which clears the stop flag.
export const mayAutoApprove = (userStopped: boolean): boolean => !userStopped;
